import { cn } from '@shared/lib';
import { TargetSentence } from '@shared/ui';
import type { ExerciseKind, SessionCard } from '../../model';
import { FittedWord } from '../FittedWord';
import { formatIpa } from '../formatIpa';

interface StageHeroProps {
  card: SessionCard;
  exercise: ExerciseKind;
  /** Ответ дан — раскрываем спрятанное слово и красим его по исходу. */
  answered: boolean;
  /** Нажали «не помню»: слово не раскрываем — на его месте прочерк. */
  gaveUp: boolean;
  /** Ответ верный. Влияет на то, что остаётся в пропуске контекста. */
  good: boolean;
  hintOpen: boolean;
  /** Текущий ввод — cloze показывает его прямо в пропуске. */
  typed: string;
}

/** Слово точками: `· · · ·` по числу букв. Первая буква открывается подсказкой. */
function masked(word: string, revealFirst: boolean): string {
  const dots = '· '.repeat(Math.max(0, word.length - (revealFirst ? 1 : 0))).trim();
  return revealFirst ? `${word[0]} ${dots}`.trim() : dots;
}

/**
 * Герой упражнения — единственный крупный объект на сцене.
 *
 * input     — простое предложение с выделенным словом (у старых и ручных карточек —
 *             само слово) и транскрипция;
 * mcq       — как input: предложение со словом (с 2026-10-07 — без него у go не понять,
 *             какой из переводов выбирать), без предложения — слово и транскрипция;
 * reverse   — русский перевод, под ним слово точками (подсказка открывает первую букву);
 * cloze     — предложение с пропуском, в который на лету попадает набранное.
 */
export function StageHero({ card, exercise, answered, gaveUp, good, hintOpen, typed }: StageHeroProps) {
  // Сдались — слово в герое не открываем: эталон и так идёт ниже, в раскрытии,
  // и два одинаковых слова подряд читались как дубль. Вместо него прочерк.
  const skipped = answered && gaveUp;

  if (exercise === 'reverse') {
    return (
      <div className="st-hero">
        <div className="st-word-ru">
          <FittedWord text={card.ru.toLowerCase()} />
        </div>
        <div
          className={cn(
            'st-masked',
            answered && 'is-open',
            skipped && 'is-skipped',
            !answered && hintOpen && 'is-hint',
          )}
        >
          {skipped ? '—' : answered ? card.en.toLowerCase() : masked(card.en, hintOpen)}
        </div>
      </div>
    );
  }

  if (exercise === 'cloze') {
    const [pre, post = ''] = (card.cloze ?? '___').split('___');
    // В пропуске остаётся то, что вписал юзер: предложение читается так, как он
    // его собрал, а эталон показывает строка «правильно» ниже. Подменяем ввод
    // словом только на верном ответе — там подмена заодно чинит опечатку.
    const filled = skipped
      ? '—'
      : answered
        ? good
          ? card.en.toLowerCase()
          : typed || '—'
        : typed || (hintOpen ? card.en[0] : '');
    return (
      <div className="st-hero">
        <div className="st-cloze">
          {pre}
          <span
            className={cn(
              'st-blank',
              answered && 'is-open',
              skipped && 'is-skipped',
              !answered && hintOpen && 'is-hint',
            )}
          >
            {filled}
            {!answered && <span className="st-caret" aria-hidden />}
          </span>
          {post}
        </div>
      </div>
    );
  }

  const ipa = formatIpa(card.ipa);
  // Прямой перевод и выбор перевода по предложению: видно, какое значение слова переводить,
  // когда ученик учит у него несколько смыслов. «Пропущено слово» выглядит так же, но
  // со словом-пропуском и другим примером.
  if ((exercise === 'input' || exercise === 'mcq') && card.simpleExample) {
    return (
      <div className="st-hero">
        <div className="st-cloze st-context" lang="en">
          <TargetSentence
            sentence={card.simpleExample}
            spans={card.simpleExampleSpans}
            word={card.en}
            markClassName="st-target"
          />
        </div>
        {ipa && <div className="st-ipa">{ipa}</div>}
      </div>
    );
  }
  return (
    <div className="st-hero">
      <div className="st-word">
        <FittedWord text={card.en.toLowerCase()} />
      </div>
      {ipa && <div className="st-ipa">{ipa}</div>}
    </div>
  );
}
